import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";

// Read-only tournament endpoints (Phase 2a, PR #694) + admin create/update
// (Phase 6.A.1 — minimal admin UI, this PR). Other admin actions (participants,
// seeds, start, override, cancel) still ship as separate phases and are driven
// by curl per the operator manual until their UI lands.
//
// All queries gated server-side by TBAC `Tournaments — View`; mutations gated
// by TBAC `Tournaments — Admin`. Until those rows exist in the Employees base
// the BFF returns 403 and we render the friendly "not enabled for your role"
// empty state.
// Appends ?tournamentRecId= (or &) to a wizard support read when the wizard is
// editing a saved tournament. See listAdminPositions below.
const withTournamentContext = (url, tournamentRecId) =>
  tournamentRecId && String(tournamentRecId).startsWith("rec")
    ? `${url}${url.includes("?") ? "&" : "?"}tournamentRecId=${encodeURIComponent(tournamentRecId)}`
    : url;

export const tournamentApi = createApi({
  reducerPath: "tournamentApi",
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: true,
  tagTypes: ["TournamentList", "Tournament", "MyTournaments", "TournamentSeason", "WizardDraft", "TournamentSeats"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${BACKEND_URL}/tournament/portal/`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      } else {
        // No-login Friend tournament editor (thread 352, step 1). The creator
        // console's READ hooks ride this same base query, so when there's no rep
        // bearer but a k:"tedit" editor token is set (the editor route stores it
        // from ?et= on mount — step 2 wires that up), authorize the read with
        // X-Editor-Token. This mirrors the BFF's checkUserOrEditorToken precedence
        // exactly (the editor path is taken ONLY when there's no bearer), so a
        // logged-in rep is never downgraded to the editor code path. Inert until
        // step 2 sets auth.editorToken — no behavior change to existing reads.
        const editorToken = getState().auth.editorToken;
        if (editorToken) headers.set("X-Editor-Token", editorToken);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    listTournaments: builder.query({
      query: () => "list",
      providesTags: ["TournamentList"],
    }),
    getMyTournaments: builder.query({ query: () => "me", providesTags: ["MyTournaments"] }),
    getTournamentById: builder.query({
      query: (recId) => `${recId}`,
      providesTags: (_result, _err, recId) => [{ type: "Tournament", id: recId }],
    }),
    createTournament: builder.mutation({
      query: (body) => ({ url: "admin/create", method: "POST", body }),
      invalidatesTags: ["TournamentList"],
    }),
    updateTournament: builder.mutation({
      query: ({ recId, body }) => ({ url: `admin/${recId}`, method: "PATCH", body }),
      invalidatesTags: (_result, _err, { recId }) => [
        "TournamentList",
        "MyTournaments",
        { type: "Tournament", id: recId },
      ],
    }),
    // Materialize bracket + flip Status -> Active. BFF requires the tournament
    // to already be in Seeding with >=2 seeded Active participants. PR 6.B.2b
    // wires the chained save (create -> /participants -> /seeds -> /start) so
    // the Save & Start button can drive this end-to-end without dropping out
    // to the operator manual.
    startTournament: builder.mutation({
      query: (recId) => ({ url: `admin/${recId}/start`, method: "POST" }),
      invalidatesTags: (_result, _err, recId) => [
        "TournamentList",
        "MyTournaments",
        { type: "Tournament", id: recId },
      ],
    }),
    // PR 6.B.2a — Position records for the wizard's Eligible Positions picker.
    // `tournamentRecId` (edit mode) names the tournament being edited: the BFF
    // then also admits a manager of THAT tournament to these wizard support
    // reads, so the Manage view's Setup tab works for a manager who does not
    // hold "Create Tournament" (thread 1a1a896a23cfff74). Same param on the
    // reps / metric-health / recruiters / conflicts / seed-preview calls.
    listAdminPositions: builder.query({
      query: (arg) => withTournamentContext("admin/positions", arg?.tournamentRecId),
    }),
    // Thread #1870 — Event tournaments. Upcoming/current Events the wizard's
    // Event picker lists, and the Event an existing tournament is bound to
    // (null when unbound). A 404 means the BFF half is not deployed yet; the
    // wizard then hides the Event type rather than offer a broken picker.
    listEventOptions: builder.query({
      query: () => "admin/event-options",
    }),
    getBoundEvent: builder.query({
      query: (recId) => `admin/${recId}/bound-event`,
    }),
    // Thread #1870 r3 — "Add everyone invited to this Event". The employee ids the
    // Event stores for its invitees — the same ids the participant picker uses.
    getEventInvitees: builder.query({
      query: (hostRecId) => `admin/event-options/${encodeURIComponent(hostRecId)}/invitees`,
    }),
    // Distinct rep names with at least `minActiveDays` logged sales-stats days
    // within the past `days` calendar days ending at `asOf` (default today).
    // The wizard intersects with this when any sales-rep position is in
    // Eligible Positions, so stale rows without recent work days drop out
    // before the bracket is built. Backfill mode passes `asOf` = past Start
    // Date and `minActiveDays` = days so a backfilled tournament filters
    // against the rep cohort that was actually working when the bracket
    // "started", not today's cohort. Backward-compatible: callers can still
    // pass a bare number for `days`-only behavior.
    listRecentlyActiveSalesReps: builder.query({
      query: (arg) => {
        const a = typeof arg === "number" ? { days: arg } : arg || {};
        const days = Number.isInteger(a.days) && a.days > 0 ? a.days : 7;
        const params = new URLSearchParams({ days: String(days) });
        if (a.asOf && /^\d{4}-\d{2}-\d{2}$/.test(a.asOf)) params.set("asOf", a.asOf);
        if (Number.isInteger(a.minActiveDays) && a.minActiveDays > 0) {
          params.set("minActiveDays", String(a.minActiveDays));
        }
        if (a.tournamentRecId) params.set("tournamentRecId", a.tournamentRecId);
        return `admin/recently-active-sales-reps?${params.toString()}`;
      },
    }),
    // PR 9.6 — Tournament rows flagged Template?=true. Powers the wizard's
    // "Load from template" picker (create mode only). Same summary shape as
    // /list, sorted by name. Lazy so the picker only fires on dropdown open.
    listTemplates: builder.query({
      query: () => "admin/templates",
    }),
    // PR 6.B.2b — chained-save mutations. BFF /participants returns `added`
    // in request order, so the form maps index -> seed for /seeds.
    addParticipants: builder.mutation({
      query: ({ recId, employeeRecIds, leadRecIds }) => ({
        url: `admin/${recId}/participants`, method: "POST", body: { employeeRecIds, leadRecIds },
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    setSeeds: builder.mutation({
      query: ({ recId, assignments }) => ({
        url: `admin/${recId}/seeds`, method: "POST", body: { assignments },
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // PR 6.B.3b — singular /seed endpoint that auto-derives seeds from
    // Tournament.Seeding Metric over Seeding Window Days. Wizard's chained
    // save calls this when a Seeding Metric is set on the row, and falls
    // back to setSeeds (selection order) otherwise.
    seedTournament: builder.mutation({
      query: (recId) => ({ url: `admin/${recId}/seed`, method: "POST" }),
      invalidatesTags: (_result, _err, recId) => [{ type: "Tournament", id: recId }],
    }),
    // Wizard rollback — calls BFF /admin/:id/cancel to flip a half-built
    // tournament to Status=Cancelled when the chained save (participants/
    // seed/start) fails mid-flight. Best-effort: if cancel itself errors,
    // the wizard surfaces both errors and the row is left in its half-built
    // state for manual cleanup.
    // Accepts either a plain recId string (wizard rollback — no reason) or
    // { recId, reason } (manual admin cancel from TournamentAdminList).
    cancelTournament: builder.mutation({
      query: (arg) => {
        const recId = typeof arg === "string" ? arg : arg.recId;
        const reason = typeof arg === "string" ? undefined : arg.reason;
        return {
          url: `admin/${recId}/cancel`,
          method: "POST",
          ...(reason != null && { body: { reason } }),
        };
      },
      invalidatesTags: (_result, _err, arg) => {
        const recId = typeof arg === "string" ? arg : arg.recId;
        return ["TournamentList", "MyTournaments", { type: "Tournament", id: recId }];
      },
    }),
    // Permanently remove a Complete or Cancelled tournament. BFF refuses any
    // in-progress status (409). For a Complete tournament the BFF also reverses
    // its payouts + Results, so invalidate TournamentSeason to refetch the rep
    // Tournament Leaderboard (season view) — the deleted tournament drops off it.
    deleteTournament: builder.mutation({
      query: (recId) => ({ url: `admin/${recId}/delete`, method: "POST" }),
      invalidatesTags: (_result, _err, recId) => [
        "TournamentList",
        "MyTournaments",
        "TournamentSeason",
        { type: "Tournament", id: recId },
      ],
    }),
    // Phase 6.E — pre-flight probe for the wizard's Save & Start. Pass one or
    // more metric keys via `?key=a&key=b`; BFF returns max(date) per source.
    // useLazyQuery so the form only fires the request when the admin actually
    // clicks Save & Start (not on every keystroke in Step 3).
    getMetricHealth: builder.query({
      query: (arg) => {
        const keys = arg && !Array.isArray(arg) && typeof arg === "object" ? arg.keys : arg;
        const list = Array.isArray(keys) ? keys : [keys];
        const qs = list.filter(Boolean).map((k) => `key=${encodeURIComponent(k)}`).join("&");
        return withTournamentContext(`admin/metric-health?${qs}`, arg?.tournamentRecId);
      },
    }),
    // Phase 9.8 — paginated audit-log read. Results are newest-first; the
    // viewer caches per-tournament so flipping back into the same edit form
    // doesn't re-fetch.
    getTournamentAuditLog: builder.query({
      query: ({ recId, limit = 50, offset = 0 }) =>
        `admin/${recId}/audit-log?limit=${limit}&offset=${offset}`,
      providesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: `${recId}-audit` }],
    }),
    // Phase 9.5 — wizard pre-flight conflict scan. Returns one row per
    // candidate participant already Active in another in-progress tournament
    // with the same Primary Metric. Mutation (not query) because the result
    // depends on a list of recIds we'd rather not embed in a query key (RTK
    // would treat each call as a fresh cache entry); the wizard fires it
    // explicitly when participants + Primary Metric are both set. POST so
    // the candidate list isn't capped by URL length.
    checkParticipantConflicts: builder.mutation({
      query: ({ employeeRecIds, primaryMetricKey, excludeTournamentRecId, tournamentRecId }) => ({
        url: "admin/check-conflicts", method: "POST",
        body: {
          employeeRecIds, primaryMetricKey, excludeTournamentRecId: excludeTournamentRecId || null,
          ...(tournamentRecId ? { tournamentRecId } : {}),
        },
      }),
    }),
    // Phase 9.1 — live seeding preview. Mutation (not query) because the
    // result depends on a candidate list we don't want in the cache key.
    // Wizard fires this on every Primary Metric / participant change in
    // Step 3 (debounced) and renders the ranked rows in a collapsible
    // panel. Read-only on the BFF side — no Seed writes happen until
    // Save & Start.
    previewSeeding: builder.mutation({
      query: ({ metricKey, candidates, windowDays, direction, seedingRollup, tournamentStartDate,
                seedingMatchDays, seedingSettleDays, excludedDays, tournamentRecId }) => ({
        url: "admin/seed-preview", method: "POST",
        body: { metricKey, candidates, windowDays, direction, seedingRollup, tournamentStartDate,
                seedingMatchDays, seedingSettleDays, excludedDays,
                ...(tournamentRecId ? { tournamentRecId } : {}) },
      }),
    }),
    // Phase 9.14 — Cross-tournament season aggregate. Returns per-rep
    // totals (wins, points won, matches played) + per-tournament summary
    // cards for every tournament whose End Date is in the requested year.
    // Server-side visibility scope (TBAC + per-tournament Visibility).
    getSeasonView: builder.query({
      query: (year) => `season/${year}`,
      providesTags: (_r, _e, year) => [
        { type: "TournamentList", id: `season-${year}` },
        "TournamentSeason",
      ],
    }),
    // Phase 9.10 — participant-side seeding leaderboard. Returns rows scoped
    // server-side per Tournaments.Seeding Score Visibility (Hidden / Own /
    // Public to Participants / Public to All). UI also gates rendering by
    // visibility (defense-in-depth) but the server is the source of truth
    // — a curl from a non-admin can't read more than visibility allows.
    getSeedingLeaderboard: builder.query({
      query: (recId) => `${recId}/seeding-leaderboard`,
      providesTags: (_r, _e, recId) => [{ type: "Tournament", id: `${recId}-seeding-lb` }],
    }),
    // Phase C — per-participant journey: stats + 8-point trend + full match
    // list. Powers the My Path tab and the ParticipantSheet body. RTK cache
    // key is `${tournamentRecId}|${participantRecId}` so switching between
    // reps in My Path keeps both warm; invalidated alongside the parent
    // Tournament tag so a bracket-level change drops the per-participant
    // entries too.
    getParticipantPath: builder.query({
      query: ({ tournamentRecId, participantRecId }) =>
        `${tournamentRecId}/participant/${participantRecId}`,
      providesTags: (_r, _e, { tournamentRecId, participantRecId }) => [
        { type: "Tournament", id: `${tournamentRecId}-path-${participantRecId}` },
        { type: "Tournament", id: tournamentRecId },
      ],
    }),
    // Per-day metric breakdown for a single match. Fetched on demand when the
    // user expands the day-breakdown section on MatchCardSheet or ParticipantStatsBlock.
    // Returns { days, primaryMetric, secondaryMetric } or { days: [] } for Manual
    // tournaments or single-day matches.
    getMatchDailyBreakdown: builder.query({
      query: ({ tournamentRecId, matchRecId }) =>
        `${tournamentRecId}/match/${matchRecId}/daily-breakdown`,
      providesTags: (_r, _e, { tournamentRecId, matchRecId }) => [
        { type: "Tournament", id: `${tournamentRecId}-daily-${matchRecId}` },
      ],
    }),
    // Phase Consolation — live consolation board for one tournament. Same
    // TBAC + per-tournament Visibility gates as getTournamentById. Polled by
    // ConsolationTab while the tab is open (matches NowTab's 30s cadence so
    // the leaderboard ticks with the rest of the page). Tag invalidates with
    // the parent Tournament tag so a bracket-level change drops this cache.
    // Phase 6: accepts { recId, divisionId } — divisionId optional; when set
    // the board is filtered to that division's participants (Division Split view).
    getConsolationLeaderboard: builder.query({
      query: ({ recId, divisionId } = {}) => {
        const base = `${recId}/consolation-leaderboard`;
        return divisionId ? `${base}?divisionId=${encodeURIComponent(divisionId)}` : base;
      },
      providesTags: (_r, _e, { recId, divisionId } = {}) => [
        { type: "Tournament", id: `${recId}-consolation${divisionId ? `-${divisionId}` : ""}` },
        { type: "Tournament", id: recId },
      ],
    }),
    // Thread #1719 round 8 — the participant-facing Standings board. The BFF has
    // served this since round 7 (`buildTournamentStandings`) with no caller, so
    // this is the first screen to read it. Ranks every eligible participant by the
    // tournament's metric composition (or its Primary Metric when none is stored)
    // over the tournament's own calendar. Pure read; no Tournament writes.
    //
    // Polls on the same 30s cadence as the other boards so a live tournament's
    // standings tick with the rest of the page.
    getStandings: builder.query({
      query: (recId) => `${recId}/standings`,
      providesTags: (_r, _e, recId) => [
        { type: "Tournament", id: `${recId}-standings` },
        { type: "Tournament", id: recId },
      ],
    }),
    // Phase 6 — per-division list with wildcard status for the division pill selector.
    // Returns [{ divisionId, divisionName, divisionOrder, wildcardStatus }].
    getConsolationDivisions: builder.query({
      query: (recId) => `${recId}/consolation/divisions`,
      providesTags: (_r, _e, recId) => [{ type: "Tournament", id: recId }],
    }),
    // Phase D — anonymous "who will win" vote tally for a Live match. Counts
    // only (server never returns the underlying voter hashes). Polled by
    // LiveMatchCard while the Now tab is open so other voters' tallies tick
    // up in roughly real time. The endpoint also reports the caller's own
    // vote so the UI can highlight which side they picked.
    getMatchTally: builder.query({
      query: (matchRecId) => `match/${matchRecId}/tally`,
      providesTags: (_r, _e, matchRecId) => [{ type: "Tournament", id: `tally-${matchRecId}` }],
    }),
    // The caller's open-match voting ballot for one tournament: every match
    // whose window is open, each tagged with the caller's own vote (myVote
    // A/B/null) + the live tally. Powers the click-through VoteCasterCard
    // (filter myVote === null). Mirror-sourced server-side (zero Airtable on
    // the request path).
    getMyBallot: builder.query({
      query: (recId) => `${recId}/ballot`,
      providesTags: (_r, _e, recId) => [{ type: "Tournament", id: `ballot-${recId}` }],
    }),
    // Phase D — cast a vote. Idempotent on same side (returns 200 with the
    // existing row); 409 on switching sides (UI surfaces the error inline
    // and does NOT update its local optimistic state). Invalidates the
    // tally tag so the panel refetches with the new count immediately.
    castMatchVote: builder.mutation({
      query: ({ matchRecId, side }) => ({
        url: `match/${matchRecId}/vote`,
        method: "POST",
        body: { side },
      }),
      invalidatesTags: (_r, _e, { matchRecId, tournamentRecId }) => [
        { type: "Tournament", id: `tally-${matchRecId}` },
        ...(tournamentRecId ? [{ type: "Tournament", id: `ballot-${tournamentRecId}` }] : []),
      ],
    }),
    // Phase E.1 — open a side-bet on a not-yet-started match. BFF creates a
    // normal Challenges row (Status="Open") with the Tournament Match + Side
    // cross-base link fields populated; the opponent then accepts it on
    // Balance like any other challenge. Phase E.2's cron auto-settles
    // Cemented bets once the linked match Status flips to Decided.
    createMatchSideBet: builder.mutation({
      query: ({ matchRecId, side, opponentRecId, stake }) => ({
        url: `match/${matchRecId}/side-bet`,
        method: "POST",
        body: { side, opponentRecId, stake },
      }),
    }),
    // Backfill drain — admin-triggered loop of tickOneTournament against a
    // single Backfill?=true row. BFF caps at 50 iterations + refuses if the
    // tournament isn't flagged Backfill?. Returns per-iteration summary so
    // the Live page can show "advanced R1, R2, completed at i=3" inline.
    // Invalidates the Tournament tag so the bracket / audit log refetch.
    runBackfillStable: builder.mutation({
      query: (recId) => ({ url: `admin/${recId}/run-backfill-stable`, method: "POST" }),
      invalidatesTags: (_result, _err, recId) => [
        "TournamentList",
        { type: "Tournament", id: recId },
        { type: "Tournament", id: `${recId}-audit` },
      ],
    }),
    // H5 — read-only dry-run summary: participant count, rounds breakdown,
    // projected bonus points + item grant count. Zero writes; no Airtable calls.
    getBackfillPreview: builder.query({
      query: (recId) => `admin/${recId}/backfill-preview`,
    }),
    // H6 — per-tournament portal settings (notify_on_backfill_complete etc.)
    getAdminSettings: builder.query({
      query: (recId) => `admin/${recId}/settings`,
      providesTags: (_r, _e, recId) => [{ type: "Tournament", id: `${recId}-settings` }],
    }),
    patchAdminSettings: builder.mutation({
      query: ({ recId, body }) => ({ url: `admin/${recId}/settings`, method: "PATCH", body }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: `${recId}-settings` }],
    }),
    patchRoundConfig: builder.mutation({
      query: ({ recId, body }) => ({ url: `admin/${recId}/round-config`, method: "PATCH", body }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    nudgeMatchParticipants: builder.mutation({
      query: (matchId) => ({ url: `admin/match/${matchId}/nudge`, method: "POST" }),
      invalidatesTags: (_r, _e, matchId) => [{ type: "Tournament", id: `tally-${matchId}` }],
    }),
    // Phase 9.8 — wizard chain resume. BFF introspects state (Draft|Seeding)
    // and continues the chain from wherever the chained save stopped.
    // Refuses Active/Complete/Cancelled (409) and Draft with <2 participants
    // (422 — admin needs to add the roster via Edit first). Returns per-step
    // results so the UI can show "seeded, then started" or "seeded, start failed".
    resumeTournament: builder.mutation({
      query: (recId) => ({ url: `admin/${recId}/resume`, method: "POST" }),
      invalidatesTags: (_result, _err, recId) => [
        "TournamentList",
        "MyTournaments",
        { type: "Tournament", id: recId },
        { type: "Tournament", id: `${recId}-audit` },
      ],
    }),
    // Phase 2 (manual placement / buy-in gate, PR #1095) — public-signup
    // applicants for one tournament, read from the MySQL signup queue (no
    // Airtable call). Server gates on `Tournaments — Admin`. status walk:
    // pending -> awaiting_placement -> placed | declined (reconciled = legacy
    // placed; failed = reconciler errored). Tagged per-tournament.
    getTournamentApplicants: builder.query({
      query: (recId) => `admin/${recId}/applicants`,
      providesTags: (_r, _e, recId) => [{ type: "Tournament", id: `${recId}-applicants` }],
    }),
    // Place one applicant into the bracket (collect the buy-in, then place).
    // BFF creates the Tournament Participant; returns 409 "still syncing" if
    // the Lead hasn't reached the Tournament base yet (~5 min). Idempotent.
    placeTournamentApplicant: builder.mutation({
      query: ({ recId, rowId, confirm }) => ({
        url: `admin/${recId}/applicants/${rowId}/place`,
        method: "POST",
        body: confirm ? { confirm: true } : {},
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: `${recId}-applicants` },
        { type: "Tournament", id: recId },
      ],
    }),
    // Decline one applicant (e.g. buy-in never paid). Terminal; no bracket
    // spot. The recruiting Sales Lead is kept. 409 if already placed.
    declineTournamentApplicant: builder.mutation({
      query: ({ recId, rowId }) => ({
        url: `admin/${recId}/applicants/${rowId}/decline`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: `${recId}-applicants` },
      ],
    }),
    // Resend the signup link to a waiting-room applicant who lost it. Works for
    // pending + awaiting_placement rows. For placed participants use
    // sendParticipantLink instead.
    resendApplicantLink: builder.mutation({
      query: ({ recId, rowId }) => ({
        url: `admin/${recId}/applicants/${rowId}/resend-link`,
        method: "POST",
      }),
    }),
    // Phase 4 (entry fees) — mark a roster participant Paid/unpaid for the
    // entry fee. body { paid, amountPaid?, paymentMethod? }; paid=false clears
    // the amount/method/timestamp. Invalidates the tournament so the roster's
    // Paid column re-reads.
    markParticipantPaid: builder.mutation({
      query: ({ recId, participantRecId, paid, amountPaid, paymentMethod }) => ({
        url: `admin/${recId}/participant/${participantRecId}/mark-paid`,
        method: "POST",
        body: { paid, amountPaid, paymentMethod },
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: recId },
      ],
    }),
    // Phase 5 payment — logged-in participant self-reports payment from the detail
    // page. Identified by JWT (no signup token/phone needed). Invalidates the
    // tournament so myParticipant.selfReported flips immediately.
    selfReportPaidAuth: builder.mutation({
      query: ({ recId, paymentMethod }) => ({
        url: `${recId}/my-payment-report`,
        method: "POST",
        body: paymentMethod ? { paymentMethod } : undefined,
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Phase 3 payment — one-tap "Remind Unpaid" push to all unpaid employee
    // participants. Returns { sent, skipped, totalUnpaid }. No cache invalidation
    // needed (no data change — pure notification fire).
    remindUnpaid: builder.mutation({
      query: ({ recId }) => ({
        url: `admin/${recId}/remind-unpaid`,
        method: "POST",
      }),
    }),
    // Phase 7 payment — manager declines a self-report claim from the confirmation
    // queue. Clears Self Reported? in the mirror + queues Airtable sync, pushes
    // a "payment not confirmed" notification to the participant. Invalidates the
    // tournament so the confirmation queue and roster badge both update.
    declineSelfReport: builder.mutation({
      query: ({ recId, participantRecId }) => ({
        url: `admin/${recId}/participant/${participantRecId}/self-report`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // #5 polish (Paid/Arrived triage badges) — day-of venue check-in toggle.
    // Mirrors mark-paid (MySQL-first, BFF PR #1190). body { arrived }.
    // Invalidates the tournament so the roster's Arrived badge flips at once.
    markParticipantArrived: builder.mutation({
      query: ({ recId, participantRecId, arrived }) => ({
        url: `admin/${recId}/participant/${participantRecId}/mark-arrived`,
        method: "POST",
        body: { arrived },
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: recId },
      ],
    }),
    // Phase 3 (wizard 9-phase build, 2026-07-02) — per-participant SMS opt-out.
    // PUT opts out (smsOptOut=true), DELETE opts back in (smsOptOut=false).
    // Both invalidate the tournament so the roster's toggle reflects immediately.
    setSmsOptOut: builder.mutation({
      query: ({ recId, participantRecId }) => ({
        url: `admin/${recId}/participant/${participantRecId}/sms-opt-out`,
        method: "PUT",
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    clearSmsOptOut: builder.mutation({
      query: ({ recId, participantRecId }) => ({
        url: `admin/${recId}/participant/${participantRecId}/sms-opt-out`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Manual bracket slot assignment (thread 19ee212ca18b9714). body { slot: number | null }.
    // null clears. Auto-swaps if target slot is occupied. DRAFT-only (BFF enforces).
    setBracketSlot: builder.mutation({
      query: ({ recId, participantRecId, slot }) => ({
        url: `admin/${recId}/participant/${participantRecId}/bracket-slot`,
        method: "POST",
        body: { slot },
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: recId },
      ],
    }),
    // Redraw round 1 from the current roster (thread 966 round 10). Match rows
    // are stored once at scaffold and never regenerated, so a bracket built
    // before a seeding fix keeps that defect with no way out. The BFF allows
    // this only while no result has been reported.
    repairBracket: builder.mutation({
      query: ({ recId }) => ({
        url: `admin/${recId}/repair-bracket`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // #5 polish (walk-up add-at-door) — admin adds one person to this
    // tournament's applicant staging list on the spot, for a walk-up who never
    // tapped the QR link. Hits the merged BFF endpoint (PR #1200), which reuses
    // the public-signup intake (MySQL-first; dedups on phone OR email, so a
    // walk-up who already signed up is refreshed, never duplicated). body
    // { first_name, last_name, phone, email? }. Invalidates the applicants list
    // so the new/refreshed row shows up.
    addWalkUpApplicant: builder.mutation({
      query: ({ recId, body }) => ({
        url: `admin/${recId}/applicants/add`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: `${recId}-applicants` },
        { type: "Tournament", id: recId },
      ],
    }),
    // Phase 12 — add an existing employee to a live tournament (Seeding or Active)
    // directly into the waiting room (Status=Waitlisted). From there the admin can
    // use Substitute (Active) or Reinstate (Seeding) to seat them. Distinct from
    // addWalkUpApplicant (which creates a name/phone applicant via the public signup
    // queue) — this takes a resolved employeeRecId and a required reason.
    addParticipantAfterStart: builder.mutation({
      query: ({ recId, employeeRecId, reason }) => ({
        url: `admin/${recId}/add-participant-after-start`,
        method: "POST",
        body: { employeeRecId, reason },
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Phase 2 (manual sign-up panel) — mint the public, no-login signup share
    // link (HMAC token over the tournament rec id) so the Step 2 panel can show
    // a copyable link for collecting Lead participants. Admin-gated server-side.
    // The hook is skipped until a saved tournament id exists (the token has
    // nothing to sign before the first Save as Draft).
    getPublicSignupLink: builder.query({
      query: (recId) => `admin/${recId}/public-signup-link`,
      providesTags: (_r, _e, recId) => [{ type: "Tournament", id: `${recId}-signup-link` }],
    }),
    // Share feature Step 3 (thread 282) — mint the 24h, read-only guest share
    // link for the page-level Share button on the tournament detail page.
    // View-gated server-side (any viewer who can see the tournament can mint).
    // Returns { url, smsBody, expiresInSeconds }; the Share button opens the
    // native SMS composer with smsBody pre-typed. Unlike public-signup-link this
    // is NOT admin-only — it lives on the rep view, gated by the tournament's
    // "Allow Sharing" toggle in the UI.
    getGuestShareLink: builder.query({
      query: (recId) => `${recId}/guest-share-link`,
      providesTags: (_r, _e, recId) => [{ type: "Tournament", id: `${recId}-guest-share-link` }],
    }),
    // Mint the no-login Friend EDITOR link for THIS tournament (queue item #3 —
    // surface editor-link sharing on the Console). Same admin-gated GET the old
    // "Live admin" page used (returns { data: { url } }); admin/bearer-only, so a
    // no-login Friend (X-Editor-Token) can't mint links — only the creator can.
    // Fetched once per Console mount and cached, which also tidies the old rough
    // edge where every "Copy editor link" tap minted a fresh live token.
    getEditorLink: builder.query({
      query: (recId) => `admin/${recId}/editor-link`,
      providesTags: (_r, _e, recId) => [{ type: "Tournament", id: `${recId}-editor-link` }],
    }),
    // Per-participant contact lookup for the manager Action menu (thread
    // 19eeb0669845842a, Levi 2026-06-21). Returns { phone, phoneDigits, name,
    // personType, friendRecId } so Send Share Link / Send Message can open a
    // pre-populated `sms:` deep-link. Lazy — only fetched when a manager opens
    // the action menu for a specific player (NOT joined into the roster GET).
    getParticipantContact: builder.query({
      query: ({ recId, participantRecId }) =>
        `admin/${recId}/participant/${participantRecId}/contact`,
      providesTags: (_r, _e, { recId, participantRecId }) => [
        { type: "Tournament", id: `${recId}-contact-${participantRecId}` },
      ],
    }),
    // Bulk-import recruiting Leads added on/after a cutoff date into this
    // tournament's applicant Staging queue (manual/concede fill — the admin then
    // places them by hand). body { since: "YYYY-MM-DD", limit? }. Invalidates the
    // applicants list so the imported rows show on the Applicants screen.
    importLeads: builder.mutation({
      query: ({ recId, since, limit, recruiterNames }) => ({
        url: `admin/${recId}/import-leads`,
        method: "POST",
        body: {
          since,
          ...(limit ? { limit } : {}),
          ...(recruiterNames && recruiterNames.length > 0 ? { recruiterNames } : {}),
        },
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: `${recId}-applicants` },
      ],
    }),
    // Lead recruiter list — distinct recruiter names from the Leads mirror.
    // Used to populate the recruiter-filter picker in the Import Leads panel.
    getLeadRecruiters: builder.query({
      query: (arg) => withTournamentContext("admin/lead-recruiters", arg?.tournamentRecId),
      providesTags: [{ type: "Tournament", id: "lead-recruiters" }],
    }),
    // Phase 2A — tournament-local Edit Name override (thread 19eeb0669845842a,
    // 2026-06-21). Sets or clears a per-tournament display name. Invalidates the
    // tournament tag so the roster re-reads with the new nameOverride surfaced
    // on each participant summary.
    setParticipantNameOverride: builder.mutation({
      query: ({ recId, participantRecId, displayName }) => ({
        url: `admin/${recId}/participant/${participantRecId}/name-override`,
        method: "PUT",
        body: { displayName },
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: recId },
      ],
    }),
    clearParticipantNameOverride: builder.mutation({
      query: ({ recId, participantRecId }) => ({
        url: `admin/${recId}/participant/${participantRecId}/name-override`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: recId },
      ],
    }),
    // Phase 2B — Add Teammate to a doubles team's empty member slot (thread
    // 19eeb0669845842a, 2026-06-21). MySQL-first: the bracket shows the new
    // partner immediately; Airtable Participant catch-up is async server-side.
    addTournamentTeammate: builder.mutation({
      query: ({ recId, teamId, body }) => ({
        url: `admin/${recId}/teams/${teamId}/add-partner`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: recId },
      ],
    }),
    // Remove an enrolled player from the tournament. Calls the BFF /withdraw
    // endpoint (Status -> Withdrew): works while Draft/Seeding/Active, and on an
    // Active bracket it forfeits the player's live match to their opponent so the
    // bracket keeps moving. reason is required (audit-logged). MySQL-first, so the
    // roster drops them instantly once the tournament tag re-reads.
    // memberOnly — ONE person left (their own Actions menu), not the team's slot.
    // A doubles teammate who stays keeps the slot with an open partner spot; a
    // partner leaving always works that way (Levi, 2026-11-04).
    waitlistParticipant: builder.mutation({
      query: ({ recId, participantRecId, memberOnly }) => ({
        url: `admin/${recId}/participant/${participantRecId}/waitlist`,
        method: "POST",
        body: memberOnly ? { memberOnly: true } : undefined,
      }),
      async onQueryStarted({ recId, participantRecId, memberOnly }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          tournamentApi.util.updateQueryData("getTournamentById", recId, (draft) => {
            if (draft?.data?.participants) {
              const parts = draft.data.participants;
              const p = parts.find((x) => x.recId === participantRecId);
              if (!p) return;
              const mate = p.teamId != null
                ? parts.find((x) => x.recId !== p.recId && x.teamId === p.teamId && x.status !== "Waitlisted")
                : null;
              const teammateStays = !!mate && (p.teamRole === "partner" || memberOnly);
              if (teammateStays) {
                if (p.teamRole === "captain") {
                  mate.teamRole = "captain";
                  if (mate.seed == null) mate.seed = p.seed;
                }
                p.teamId = null; p.teamName = null; p.teamRole = null;
              } else if (mate && p.teamRole === "captain") {
                // The slot's own Remove: the whole team goes, as the server does.
                mate.status = "Waitlisted"; mate.seed = null;
              }
              p.status = "Waitlisted"; p.seed = null;
            }
          }),
        );
        try { await queryFulfilled; } catch { patch.undo(); }
      },
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: recId },
      ],
    }),
    reinstateParticipant: builder.mutation({
      query: ({ recId, participantRecId, reason }) => ({
        url: `admin/${recId}/participant/${participantRecId}/reinstate`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: recId },
      ],
    }),
    assignPartner: builder.mutation({
      query: ({ recId, teamId, participantRecId }) => ({
        url: `admin/${recId}/assign-partner`,
        method: "POST",
        body: { teamId, participantRecId },
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: recId },
      ],
    }),
    withdrawParticipant: builder.mutation({
      query: ({ recId, participantRecId, reason }) => ({
        url: `admin/${recId}/participant/${participantRecId}/withdraw`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: recId },
      ],
    }),
    disqualifyParticipant: builder.mutation({
      query: ({ recId, participantRecId, reason }) => ({
        url: `admin/${recId}/participant/${participantRecId}/disqualify`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: recId },
      ],
    }),
    substituteParticipant: builder.mutation({
      query: ({ recId, participantRecId, incomingEmployeeRecId, reason }) => ({
        url: `admin/${recId}/participant/${participantRecId}/substitute`,
        method: "POST",
        body: { incomingEmployeeRecId, reason },
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: recId },
      ],
    }),
    easyFill: builder.mutation({
      query: ({ recId, basis }) => ({
        url: `admin/${recId}/easy-fill`,
        method: "POST",
        body: { basis },
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "Tournament", id: recId },
      ],
    }),
    // Change bracket size pre-start. Growing adds empty slots; shrinking sends
    // overflow participants to the Waiting Room. body: { newSize }
    changeBracketSize: builder.mutation({
      query: ({ recId, newSize }) => ({
        url: `admin/${recId}/change-bracket-size`,
        method: "POST",
        body: { newSize },
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Promote Waiting Room participants into new division capacity pre-start.
    // body: { divisionMode: "fill_all" | "fill_one" }
    addDivisionFromWaitingRoom: builder.mutation({
      query: ({ recId, divisionMode = "fill_all" }) => ({
        url: `admin/${recId}/divisions/add`,
        method: "POST",
        body: { divisionMode },
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Delete a pre-start division slot, returning all participants to the WR.
    // body: { divisionOrder? } — defaults to the last division on the server.
    deleteDivisionPreStart: builder.mutation({
      query: ({ recId, divisionOrder }) => ({
        url: `admin/${recId}/divisions/delete`,
        method: "POST",
        body: { divisionOrder },
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Voting leaderboard (per-tournament) — named pick-accuracy ranking for one
    // tournament. Same TBAC gate as getTournamentById. Tagged with the parent
    // Tournament tag so a match decision (which can re-score picks) drops this
    // cache alongside the bracket.
    getVotingLeaderboard: builder.query({
      query: (recId) => `${recId}/voting-leaderboard`,
      providesTags: (_r, _e, recId) => [
        { type: "Tournament", id: `${recId}-voting-lb` },
        { type: "Tournament", id: recId },
      ],
    }),
    // Arena-wide voting leaderboard — pick-accuracy across all visible
    // tournaments. Powers the home section. Tagged on TournamentList so a new
    // tournament / bracket change refreshes it with the rest of the Arena home.
    getAllVotingLeaderboard: builder.query({
      query: () => `all-voting-leaderboard`,
      providesTags: [{ type: "TournamentList", id: "arena-voting-lb" }],
    }),
    // Arena "auto-correct + notify" master switch (TBAC `Tournaments — Admin`).
    // Read returns { flipApplyAuto, updatedBy, updatedAt }; mutation flips it.
    getArenaSettings: builder.query({
      query: () => "admin/arena-settings",
      providesTags: [{ type: "Tournament", id: "arena-settings" }],
    }),
    setArenaSettings: builder.mutation({
      query: (body) => ({ url: "admin/arena-settings", method: "POST", body }),
      invalidatesTags: [{ type: "Tournament", id: "arena-settings" }],
    }),
    // Friend-editor invite — text the no-login editor link for THIS tournament to
    // one or several Friends (name optional + phone). Same endpoint the live
    // admin page uses; body { recipients: [{ name, phone }] }. The link is minted
    // server-side against the saved tournament id, so this is only callable once a
    // recId exists (first Save as Draft). No cache to invalidate (the invite log
    // lives on the live admin page).
    //
    // Thread 1a1a896a23cfff74: this ADDS each recipient as a manager of the
    // tournament (portal_app.tournament_managers) and then texts them, so it
    // refreshes the Console's managers list. Creator- or admin-only on the BFF.
    sendEditorLink: builder.mutation({
      query: ({ recId, recipients = [], employeeManagerRecIds }) => ({
        url: `admin/${recId}/editor-link/send`,
        method: "POST",
        body: {
          recipients,
          ...(Array.isArray(employeeManagerRecIds) && employeeManagerRecIds.length > 0
            ? { employeeManagerRecIds }
            : {}),
        },
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: `${recId}-managers` }],
    }),
    // The tournament's managers (thread 1a1a896a23cfff74) — { managers, canChange }.
    // canChange = this viewer is an admin or the creator, the only people who
    // may add or remove a manager.
    getTournamentManagers: builder.query({
      query: (recId) => `admin/${recId}/managers`,
      providesTags: (_r, _e, recId) => [{ type: "Tournament", id: `${recId}-managers` }],
    }),
    // Remove one manager. Their next read of the tournament answers canEdit=false,
    // so their page drops back to the normal view.
    removeTournamentManager: builder.mutation({
      query: ({ recId, managerId }) => ({ url: `admin/${recId}/managers/${managerId}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: `${recId}-managers` }],
    }),
    // Issue 4 PR-D (Levi 2026-06-21, thread 491). Mint+text a fresh participant
    // link for ONE participant in this tournament. Shared by two callers — the
    // manager's per-player ⚡ "Send Participant Link" item and the participant's
    // own ⚡ "Get My Link" item. The BFF middleware admits the bearer (manager),
    // X-Editor-Token (Friend manager on the no-login editor page), OR
    // X-Participant-Token (the participant themselves, scope-locked to self).
    // Body: optional { phone } override when the manager wants to type a number
    // that fetchParticipantContact couldn't surface (walk-up without a phone on
    // file). Response carries `data.url` ONLY for the participant self-send so
    // the UI can offer Copy / native Share alongside the SMS deliverable.
    sendParticipantLink: builder.mutation({
      query: ({ recId, participantRecId, phone }) => ({
        url: `admin/${recId}/participant/${participantRecId}/send-link`,
        method: "POST",
        body: phone ? { phone } : {},
      }),
    }),
    // Notification Hub (thread 569). Bulk SMS to tournament groups with per-role
    // magic links. getHubRecipients resolves who can be texted; sendHubNotification
    // fires the SMS batch. Both are editor-token gated so Friend editors can also use
    // the hub. Query params for recipients: groups, paid (0|1), here (0|1).
    getHubRecipients: builder.query({
      query: ({ recId, groups, paid, here }) => {
        const params = new URLSearchParams();
        if (groups && groups.length) params.set("groups", groups.join(","));
        if (paid !== undefined && paid !== null) params.set("paid", paid ? "1" : "0");
        if (here !== undefined && here !== null) params.set("here", here ? "1" : "0");
        const qs = params.toString();
        return `admin/${recId}/hub/recipients${qs ? `?${qs}` : ""}`;
      },
    }),
    sendHubNotification: builder.mutation({
      query: ({ recId, recipients, message }) => ({
        url: `admin/${recId}/hub/send`,
        method: "POST",
        body: { recipients, message },
      }),
    }),
    // Missing 1 (thread 569 batch) — send history panel. Returns hub sends.
    // Pass limit to load more rows when "View all" is expanded (BFF caps at 50).
    getHubSendHistory: builder.query({
      query: ({ recId, limit }) =>
        `admin/${recId}/hub/send-history${limit && limit !== 10 ? `?limit=${limit}` : ""}`,
    }),
    // Doubles teams (Phase 5a CRUD). Reads ride getTournamentById's additive
    // teams[] — these are the writes. All gated by the BFF's checkUserOrEditorToken,
    // so prepareHeaders authorizes them with the rep bearer OR the no-login editor
    // token transparently. Each invalidates the parent Tournament tag so the
    // bracket chips + Roster Teams panel re-read off the one getTournament cache.
    createTeam: builder.mutation({
      query: ({ recId, teamName, member1ParticipantRecId, member2ParticipantRecId }) => ({
        url: `admin/${recId}/teams`,
        method: "POST",
        body: { teamName, member1ParticipantRecId, member2ParticipantRecId },
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    updateTeam: builder.mutation({
      query: ({ recId, teamId, ...fields }) => ({
        url: `admin/${recId}/teams/${teamId}`,
        method: "PATCH",
        body: fields,
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    deleteTeam: builder.mutation({
      query: ({ recId, teamId }) => ({
        url: `admin/${recId}/teams/${teamId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // 5d-UI — board check-in toggle. A collapsed doubles team plays as one
    // bracket slot, so the partner has no per-match check-in; this stamps each
    // member present/absent on the team row (MySQL-first, BFF Phase 5d). body
    // { participantRecId, checkedIn }; checkedIn=false undoes a mis-tap.
    teamCheckIn: builder.mutation({
      query: ({ recId, teamId, participantRecId, checkedIn }) => ({
        url: `admin/${recId}/teams/check-in`,
        method: "POST",
        body: { teamId, participantRecId, checkedIn },
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // 5b-2 — mint (and optionally text) a no-login "join my doubles team" link for
    // a PENDING team's open slot. Same checkUserOrEditorToken gate as the rest of
    // team CRUD, so prepareHeaders authorizes it with the rep bearer OR the
    // no-login editor token transparently. body { partnerParticipantRecId, phone? };
    // returns { url, smsBody, texted, ... }. The k:"tjoin" link the BFF mints bakes
    // in this exact tournament+team+partner — the partner's no-login confirm page
    // (TournamentJoinPartner) can only ever fill THAT slot with THAT person. No
    // cache to invalidate here — member2 fills when the partner taps the link, and
    // the existing getTournament poll/refetch surfaces it.
    sendTeamInvite: builder.mutation({
      query: ({ recId, teamId, partnerParticipantRecId, phone }) => ({
        url: `admin/${recId}/teams/${teamId}/invite`,
        method: "POST",
        body: { partnerParticipantRecId, phone },
      }),
    }),
    // Phase 2 doubles — participant leaves their own team. Captain with no partner
    // deletes the team; captain with partner promotes the partner; member2 clears slot.
    leaveTeam: builder.mutation({
      query: ({ recId, participantRecId }) => ({
        url: `admin/${recId}/participant/${participantRecId}/leave-team`,
        method: "POST",
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Thread 966 — admin removes player from their doubles team (restructures team, renames to Unnamed, waitlists if Active).
    adminRemoveFromTeam: builder.mutation({
      query: ({ recId, participantRecId }) => ({
        url: `admin/${recId}/participant/${participantRecId}/admin-remove-from-team`,
        method: "POST",
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Phase 4 — team payment flow.
    uploadTeamPaymentProof: builder.mutation({
      query: ({ recId, file }) => {
        const form = new FormData();
        form.append("file", file);
        return { url: `admin/${recId}/teams/payment/proof`, method: "POST", body: form };
      },
    }),
    reportTeamPayment: builder.mutation({
      query: ({ recId, teamId, paymentMethod, proofUrl }) => ({
        url: `admin/${recId}/teams/${teamId}/payment`,
        method: "POST",
        body: { paymentMethod, proofUrl },
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    verifyTeamPayment: builder.mutation({
      query: ({ recId, teamId, verified }) => ({
        url: `admin/${recId}/teams/${teamId}/payment/verify`,
        method: "PATCH",
        body: { verified },
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    adminMarkTeamPaid: builder.mutation({
      query: ({ recId, teamId, paymentMethod }) => ({
        url: `admin/${recId}/teams/${teamId}/payment/admin-mark`,
        method: "PATCH",
        body: { paymentMethod },
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    adminResetTeamPayment: builder.mutation({
      query: ({ recId, teamId }) => ({
        url: `admin/${recId}/teams/${teamId}/payment`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Phase 5 — participant self-check-in for tournament-level doubles check-in.
    // checkinMode='tournament': each member taps once before bracket lock.
    // body { participantRecId?, checkedIn }; tpart token self-scopes to self.
    participantTeamCheckIn: builder.mutation({
      query: ({ recId, teamId, participantRecId, checkedIn }) => ({
        url: `admin/${recId}/teams/${teamId}/checkin/tournament`,
        method: "POST",
        body: { participantRecId, checkedIn },
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Phase 5 — admin bracket-lock-gate status. Returns 3 gates (payment, checkin,
    // solos) each with { required, overridden, status, ...counts } + allClear bool.
    getBracketLockGate: builder.query({
      query: (recId) => `admin/${recId}/bracket-lock-gate`,
      providesTags: (_r, _e, recId) => [{ type: "Tournament", id: recId }],
    }),
    // Phase 5 — admin acknowledge an unmet gate to proceed anyway.
    // body { gate: 'payment'|'checkin'|'solos', override: boolean }
    overrideBracketLockGate: builder.mutation({
      query: ({ recId, gate, override }) => ({
        url: `admin/${recId}/bracket-lock-gate/override`,
        method: "POST",
        body: { gate, override },
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Phase 5 per-match doubles check-in — used from the live match surface when
    // checkinMode='per_match'. Uses the match token (same path as match/* routes).
    matchDoublesTeamCheckIn: builder.mutation({
      query: ({ matchId, slot, checkedIn }) => ({
        url: `match/${matchId}/doubles-check-in`,
        method: "POST",
        body: { slot, checkedIn },
      }),
    }),
    // Phase 3 — search open teams by team code or name.
    searchTeams: builder.query({
      query: ({ recId, q, openOnly = true }) => ({
        url: `admin/${recId}/teams/search`,
        params: { q, openOnly: openOnly ? "true" : "false" },
      }),
    }),
    // Phase 3 — join a pending team found via search.
    joinTeamBySearch: builder.mutation({
      query: ({ recId, teamId, participantRecId }) => ({
        url: `admin/${recId}/teams/${teamId}/join`,
        method: "POST",
        body: { participantRecId },
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    getCreatorBalance: builder.query({
      query: (recId) => `admin/${recId}/creator-balance`,
    }),
    getMyBalance: builder.query({
      query: () => `admin/my-balance`,
    }),
    getRoundPrizes: builder.query({
      query: (recId) => `admin/${recId}/round-prizes`,
    }),
    setRoundPrizes: builder.mutation({
      query: ({ recId, prizes, totalAwarded }) => ({
        url: `admin/${recId}/round-prizes`,
        method: "PUT",
        body: { prizes, ...(totalAwarded != null ? { totalAwarded } : {}) },
      }),
      // Saving prizes also upserts tournament_wizard_totals (totalAwarded) on the
      // BFF. Invalidate the tournament cache so the header "pts pool" figure
      // refreshes immediately after the wizard saves — without this the cached
      // tournament response keeps the pre-save totalAwarded (null → falls back to
      // totalPointsPool) even after a successful prize save.
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    getAdminPrizeGrants: builder.query({
      query: (recId) => `admin/${recId}/prize-grants`,
      providesTags: (_r, _e, recId) => [{ type: "AdminPrizeGrants", id: recId }],
    }),
    adminGrantRoundPrize: builder.mutation({
      query: ({ tournamentRecId, ...body }) => ({
        url: `admin/${tournamentRecId}/prize-grants`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { tournamentRecId }) => [{ type: "AdminPrizeGrants", id: tournamentRecId }],
    }),
    adminRevokeRoundPrize: builder.mutation({
      query: ({ tournamentRecId, grantId }) => ({
        url: `admin/${tournamentRecId}/prize-grants/${grantId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { tournamentRecId }) => [{ type: "AdminPrizeGrants", id: tournamentRecId }],
    }),
    repairPrizeGrants: builder.mutation({
      query: (tournamentRecId) => ({
        url: `admin/${tournamentRecId}/repair-prize-grants`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, tournamentRecId) => [{ type: "AdminPrizeGrants", id: tournamentRecId }],
    }),
    // Phase 7 — admin list audit log. Last 3 entries per tournament; cached
    // on TournamentList so it refreshes whenever a tournament is mutated.
    getRecentTournamentActivity: builder.query({
      query: () => "admin/recent-activity",
      providesTags: ["TournamentList"],
    }),
    // Phase 9 — server-side wizard draft (cross-device persistence).
    // One draft slot per admin; snapshot = same shape as the localStorage blob.
    getWizardDraft: builder.query({
      query: () => "admin/wizard-draft",
      providesTags: ["WizardDraft"],
    }),
    upsertWizardDraft: builder.mutation({
      query: (snapshot) => ({ url: "admin/wizard-draft", method: "PUT", body: { snapshot } }),
      invalidatesTags: ["WizardDraft"],
    }),
    clearWizardDraft: builder.mutation({
      query: () => ({ url: "admin/wizard-draft", method: "DELETE" }),
      invalidatesTags: ["WizardDraft"],
    }),
    // Phase 8 doubles — injury sub: swap a team member.
    substituteTeamMember: builder.mutation({
      query: ({ recId, teamId, outRecId, inRecId }) => ({
        url: `admin/${recId}/teams/${teamId}/substitute`,
        method: "POST",
        body: { outRecId, inRecId },
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Phase 8 doubles — ghost link: bind a ghost placeholder slot to a real portal account.
    linkGhostToPortal: builder.mutation({
      query: ({ recId, teamId, ghostRecId, portalRecId }) => ({
        url: `admin/${recId}/teams/${teamId}/link-ghost`,
        method: "POST",
        body: { ghostRecId, portalRecId },
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Phase 8 doubles — admin view of expired invites.
    getExpiredInvites: builder.query({
      query: (recId) => `admin/${recId}/teams/invites/expired`,
    }),
    // Phase 8 doubles — prior partner shortcut for the invite quick-fill.
    getPriorPartner: builder.query({
      query: ({ recId, participantRecId }) => ({
        url: `admin/${recId}/teams/prior-partner`,
        params: { participantRecId },
      }),
    }),
    // Item 2 — waitlisted team captain confirms they accept the offered spot.
    acceptWaitlistOffer: builder.mutation({
      query: ({ recId, participantRecId }) => ({
        url: `admin/${recId}/doubleteam/waitlist/accept`,
        method: "POST",
        body: { participantRecId },
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Item 46 — admin promotes a waitlisted team to the front of the queue.
    promoteWaitlistTeam: builder.mutation({
      query: ({ recId, teamId }) => ({
        url: `admin/${recId}/waitlist/${teamId}/promote`,
        method: "POST",
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Bug 48 — bulk invite: send invites to multiple partners in one call.
    // BFF: POST /tournament/:recId/teams/:teamId/invite-bulk
    sendBulkInvite: builder.mutation({
      query: ({ recId, teamId, invites }) => ({
        url: `tournament/${recId}/teams/${teamId}/invite-bulk`,
        method: "POST",
        body: { invites },
      }),
      invalidatesTags: (_result, _err, { recId }) => [{ type: "Tournament", id: recId }],
    }),
    // Phase 3 (thread 966) — Named-seat model roster display. Returns all seats
    // for a tournament joined to their assigned team. model_version='seats' only;
    // safe to call on legacy tournaments (returns []).
    getTournamentSeats: builder.query({
      query: (recId) => `admin/${recId}/seats`,
      providesTags: (_r, _e, recId) => [{ type: "TournamentSeats", id: recId }],
    }),
    // Phase 4 (thread 966) — Seat assignment mutations.
    // assignTeamToSeat: POST admin/:id/seats/:seatId/assign { teamId }
    //   Atomically places a team onto a seat (evicts any prior holder to WR).
    // clearSeat: DELETE admin/:id/seats/:seatId/assign
    //   Sends the assigned team back to the waiting room.
    assignTeamToSeat: builder.mutation({
      query: ({ recId, seatId, teamId }) => ({
        url: `admin/${recId}/seats/${seatId}/assign`,
        method: "POST",
        body: { teamId },
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "TournamentSeats", id: recId },
        { type: "Tournament", id: recId },
      ],
    }),
    clearSeat: builder.mutation({
      query: ({ recId, seatId }) => ({
        url: `admin/${recId}/seats/${seatId}/assign`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { recId }) => [
        { type: "TournamentSeats", id: recId },
        { type: "Tournament", id: recId },
      ],
    }),
    // Phase 6 (thread 966) — Seats configuration UI.
    // generateSeats: POST admin/:id/seats/generate { divisions }
    //   Bulk-creates seat rows. divisions = [{division, count}, ...]
    //   Safe to call repeatedly (INSERT IGNORE).
    generateSeats: builder.mutation({
      query: ({ recId, divisions }) => ({
        url: `admin/${recId}/seats/generate`,
        method: "POST",
        body: { divisions },
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "TournamentSeats", id: recId }],
    }),
    // patchDoublesConfig: PATCH admin/:id/doubles-config { ...patch }
    //   Partial update to the doubles config (modelVersion, seedingMode, etc.).
    //   Invalidates Tournament so isSeatsModel re-derives after enable.
    patchDoublesConfig: builder.mutation({
      query: ({ recId, ...patch }) => ({
        url: `admin/${recId}/doubles-config`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (_r, _e, { recId }) => [{ type: "Tournament", id: recId }],
    }),
  }),
});

export const {
  useListTournamentsQuery,
  useGetMyTournamentsQuery,
  useGetTournamentByIdQuery,
  useCreateTournamentMutation,
  useUpdateTournamentMutation,
  useStartTournamentMutation,
  useListAdminPositionsQuery,
  useListRecentlyActiveSalesRepsQuery,
  useLazyListTemplatesQuery,
  useAddParticipantsMutation,
  useSetSeedsMutation,
  useSeedTournamentMutation,
  useCancelTournamentMutation,
  useDeleteTournamentMutation,
  useLazyGetMetricHealthQuery,
  useGetTournamentAuditLogQuery,
  useCheckParticipantConflictsMutation,
  usePreviewSeedingMutation,
  useGetSeedingLeaderboardQuery,
  useGetSeasonViewQuery,
  useGetParticipantPathQuery,
  useGetMatchDailyBreakdownQuery,
  useGetConsolationLeaderboardQuery,
  useGetStandingsQuery,
  useGetConsolationDivisionsQuery,
  useGetMatchTallyQuery,
  useGetMyBallotQuery,
  useCastMatchVoteMutation,
  useCreateMatchSideBetMutation,
  useRunBackfillStableMutation,
  useResumeTournamentMutation,
  useGetTournamentApplicantsQuery,
  usePlaceTournamentApplicantMutation,
  useDeclineTournamentApplicantMutation,
  useResendApplicantLinkMutation,
  useMarkParticipantPaidMutation,
  useSelfReportPaidAuthMutation,
  useRemindUnpaidMutation,
  useDeclineSelfReportMutation,
  useMarkParticipantArrivedMutation,
  useSetSmsOptOutMutation,
  useClearSmsOptOutMutation,
  useSetBracketSlotMutation,
  useRepairBracketMutation,
  useAddWalkUpApplicantMutation,
  useGetPublicSignupLinkQuery,
  useGetGuestShareLinkQuery,
  useGetEditorLinkQuery,
  useGetParticipantContactQuery,
  useLazyGetParticipantContactQuery,
  useImportLeadsMutation,
  useGetLeadRecruitersQuery,
  useGetTournamentManagersQuery,
  useRemoveTournamentManagerMutation,
  useSetParticipantNameOverrideMutation,
  useClearParticipantNameOverrideMutation,
  useAddTournamentTeammateMutation,
  useWaitlistParticipantMutation,
  useReinstateParticipantMutation,
  useAssignPartnerMutation,
  useWithdrawParticipantMutation,
  useEasyFillMutation,
  useChangeBracketSizeMutation,
  useAddDivisionFromWaitingRoomMutation,
  useDeleteDivisionPreStartMutation,
  useGetVotingLeaderboardQuery,
  useGetAllVotingLeaderboardQuery,
  useGetArenaSettingsQuery,
  useSetArenaSettingsMutation,
  useSendEditorLinkMutation,
  useSendParticipantLinkMutation,
  useGetHubRecipientsQuery,
  useLazyGetHubRecipientsQuery,
  useSendHubNotificationMutation,
  useGetHubSendHistoryQuery,
  useLazyGetHubSendHistoryQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useTeamCheckInMutation,
  useSendTeamInviteMutation,
  useLeaveTeamMutation,
  useAdminRemoveFromTeamMutation,
  useUploadTeamPaymentProofMutation,
  useReportTeamPaymentMutation,
  useVerifyTeamPaymentMutation,
  useAdminMarkTeamPaidMutation,
  useAdminResetTeamPaymentMutation,
  useSearchTeamsQuery,
  useLazySearchTeamsQuery,
  useJoinTeamBySearchMutation,
  useGetCreatorBalanceQuery,
  useGetMyBalanceQuery,
  useGetRoundPrizesQuery,
  useSetRoundPrizesMutation,
  useGetAdminPrizeGrantsQuery,
  useAdminGrantRoundPrizeMutation,
  useAdminRevokeRoundPrizeMutation,
  useRepairPrizeGrantsMutation,
  useGetRecentTournamentActivityQuery,
  useGetWizardDraftQuery,
  useUpsertWizardDraftMutation,
  useClearWizardDraftMutation,
  useDisqualifyParticipantMutation,
  useSubstituteParticipantMutation,
  useAddParticipantAfterStartMutation,
  useGetBackfillPreviewQuery,
  useLazyGetBackfillPreviewQuery,
  useGetAdminSettingsQuery,
  usePatchAdminSettingsMutation,
  usePatchRoundConfigMutation,
  useNudgeMatchParticipantsMutation,
  useParticipantTeamCheckInMutation,
  useGetBracketLockGateQuery,
  useLazyGetBracketLockGateQuery,
  useOverrideBracketLockGateMutation,
  useMatchDoublesTeamCheckInMutation,
  useSubstituteTeamMemberMutation,
  useLinkGhostToPortalMutation,
  useGetExpiredInvitesQuery,
  useLazyGetExpiredInvitesQuery,
  useLazyGetPriorPartnerQuery,
  useAcceptWaitlistOfferMutation,
  usePromoteWaitlistTeamMutation,
  useSendBulkInviteMutation,
  useGetTournamentSeatsQuery,
  useAssignTeamToSeatMutation,
  useClearSeatMutation,
  useGenerateSeatsMutation,
  usePatchDoublesConfigMutation,
  useListEventOptionsQuery,
  useGetBoundEventQuery,
  useLazyGetEventInviteesQuery,
} = tournamentApi;
